import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container, Box, Typography, Button,
    CircularProgress, Chip,
    IconButton, Tooltip, Dialog,
    DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    ShoppingBag as ShoppingBagIcon,
    LocalShipping as ShippingIcon,
    Receipt as ReceiptIcon,
    Cancel as CancelIcon,
    ContentCopy as CopyIcon,
    BrokenImage as NoImageIcon,
    LocationOn as LocationIcon,
    CreditCard as PaymentIcon,
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../services/api';
import { StatusChip } from './CustomerOrderlist';
import '../styles/ProtectedRoutes.css';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (val, digits = 2) => parseFloat(val || 0).toFixed(digits);

const fmtDate = (raw) => {
    if (!raw) return '—';
    return new Date(raw).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const NON_CANCELLABLE = ['Shipped', 'Out For Delivery', 'Delivered', 'Cancelled'];

// ── Section wrapper ────────────────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
    <Box sx={{
        border: '2px solid rgba(188, 150, 94, 0.42)',
        borderRadius: 2.5,
        overflow: 'hidden',
        mb: 2,
        backdropFilter: 'blur(8px)',
        background: 'rgba(255,255,255,0.02)',
    }}>
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: { xs: 2, sm: 2.5 }, py: 1.5,
            background: 'rgba(188,150,94,0.06)',
            borderBottom: '1px solid rgba(188,150,94,0.1)',
        }}>
            {icon && React.cloneElement(icon, { sx: { fontSize: 14, color: 'rgba(188,150,94,0.65)' } })}
            <Typography sx={{
                fontSize: '0.68rem', fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '0.1em',
                color: 'rgba(188,150,94,0.65)',
            }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
            {children}
        </Box>
    </Box>
);

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, mono = false, copy = false, last = false }) => {
    const handleCopy = () => navigator.clipboard.writeText(String(value));
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            py: { xs: 1, sm: 1.1 },
            borderBottom: last ? 'none' : '1px solid rgba(188, 150, 94, 0.62)',
            gap: 2,
        }}>
            <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.8rem' }, color: '#ffffff', flexShrink: 0, fontWeight: 500 }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {typeof value === 'string' || typeof value === 'number' ? (
                    <Typography sx={{
                        fontSize: { xs: '0.82rem', sm: '0.85rem' },
                        color: mono ? '#bc965e' : '#ffffff',
                        fontFamily: mono ? 'monospace' : 'inherit',
                        fontWeight: mono ? 700 : 500,
                        textAlign: 'right',
                        letterSpacing: mono ? '0.03em' : 'normal',
                        wordBreak: 'break-all',
                    }}>
                        {value || '—'}
                    </Typography>
                ) : value}
                {copy && value && (
                    <Tooltip title="Copy" placement="top">
                        <IconButton size="small" onClick={handleCopy} sx={{
                            p: 0.3, color: 'rgba(188,150,94,0.4)', flexShrink: 0,
                            '&:hover': { color: '#bc965e', background: 'rgba(188,150,94,0.1)' },
                        }}>
                            <CopyIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

// ── Table cells ────────────────────────────────────────────────────────────────
const TH = ({ children, align = 'left', width }) => (
    <TableCell align={align} sx={{
        width, py: 1.25, px: { xs: 1.5, sm: 2 },
        fontSize: '0.65rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: '#ffffff',
        borderBottom: '1px solid rgba(188, 150, 94, 0.45)',
        background: 'rgba(188,150,94,0.04)',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </TableCell>
);

const TD = ({ children, align = 'left', sx: sxProp = {} }) => (
    <TableCell align={align} sx={{
        py: { xs: 1.25, sm: 1.5 }, px: { xs: 1.5, sm: 2 },
        borderBottom: '1px solid rgba(188, 150, 94, 0.54)',
        verticalAlign: 'middle',
        ...sxProp,
    }}>
        {children}
    </TableCell>
);

// ── Items Table ───────────────────────────────────────────────────────────────
const ItemsTable = ({ items, orderTotal }) => {
    const subtotal = useMemo(
        () => items.reduce((s, i) => s + parseFloat(i.Amount || 0), 0),
        [items]
    );

      const getMediaUrl = (filePath) => {
        if (!filePath) return '';
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') ;
        return `${backendUrl}${filePath}`;
    };
    
    return (
        <Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                        <TableRow>
                            <TH width="40%">Product</TH>
                            <TH width="15%">Size</TH>
                            <TH align="center" width="10%">Qty</TH>
                            <TH align="right" width="15%">Unit Price</TH>
                            <TH align="right" width="15%">Total</TH>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, idx) => (
                            <TableRow key={item.BillItemId || idx} sx={{
                                '&:last-child td': { borderBottom: 'none' },
                                '&:hover': { background: 'rgba(188,150,94,0.03)' },
                                transition: 'background 0.15s',
                            }}>
                                <TD>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{
                                            width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 },
                                            flexShrink: 0, borderRadius: 0.5, overflow: 'hidden',
                                            border: '1px solid rgba(188,150,94,0.18)',
                                            background: 'rgba(188,150,94,0.05)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {(() => {
                                                const photoUrl = getMediaUrl(item.Photo);
                                               
                                                return photoUrl ? (
                                                    <img
                                                        src={photoUrl}
                                                        alt={item.ProductName}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={e => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <NoImageIcon sx={{ fontSize: 18, color: 'rgba(188,150,94,0.25)' }} />
                                                );
                                            })()}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{
                                                fontSize: { xs: '0.78rem', sm: '0.82rem' }, fontWeight: 600,
                                                color: 'rgba(255,255,255,0.85)', overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                maxWidth: { xs: 110, sm: 160 },
                                            }}>
                                                {item.ProductName || '—'}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.65rem', color: 'rgba(255,255,255,0.76)',
                                                fontFamily: 'monospace', letterSpacing: '0.03em',
                                            }}>
                                                {item.ProductCode}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TD>
                                <TD>
                                    {item.Size ? (
                                        <Chip label={item.Size} size="small" sx={{
                                            height: 20, fontSize: '0.63rem', fontWeight: 700,
                                            letterSpacing: '0.04em', textTransform: 'uppercase',
                                            backgroundColor: 'rgba(188,150,94,0.08)',
                                            border: '1px solid rgba(188,150,94,0.2)',
                                            color: '#ffffff', '& .MuiChip-label': { px: '7px' },
                                        }} />
                                    ) : (
                                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)' }}>—</Typography>
                                    )}
                                </TD>
                                <TD align="center">
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        minWidth: 28, height: 24, borderRadius: 1,
                                        background: 'rgba(188,150,94,0.08)',
                                        border: '1px solid rgba(188,150,94,0.15)',
                                    }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#ffffff' }}>
                                            {item.Quantity}
                                        </Typography>
                                    </Box>
                                </TD>
                                <TD align="right">
                                    <Typography sx={{ fontSize: '0.78rem', color: '#ffffff', fontFamily: 'monospace' }}>
                                        ₹{fmt(item.Rate)}
                                    </Typography>
                                </TD>
                                <TD align="right">
                                    <Typography sx={{
                                        fontSize: { xs: '0.82rem', sm: '0.86rem' }, fontWeight: 700,
                                        color: '#bc965e', fontFamily: 'monospace', letterSpacing: '0.02em',
                                    }}>
                                        ₹{fmt(item.Amount)}
                                    </Typography>
                                </TD>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{
                display: 'flex', justifyContent: 'flex-end', pt: 1.5, pb: 0.5,
                px: { xs: 1.5, sm: 2 }, borderTop: '1px solid rgba(188,150,94,0.1)',
            }}>
                <Box sx={{ minWidth: { xs: 160, sm: 200 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                        <Typography sx={{ fontSize: '0.74rem', color: '#ffffff' }}>
                            Subtotal ({items.length} item{items.length !== 1 ? 's' : ''})
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: '#ffffff', fontFamily: 'monospace' }}>
                            ₹{fmt(subtotal)}
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        pt: 0.75, borderTop: '1px solid rgba(188,150,94,0.12)',
                    }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#ffffff' }}>
                            Order Total
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: '1rem', sm: '1.05rem' }, fontWeight: 800,
                            color: '#bc965e', fontFamily: 'monospace', letterSpacing: '0.03em',
                        }}>
                            ₹{fmt(orderTotal)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// ── Cancel Dialog ─────────────────────────────────────────────────────────────
const CancelDialog = ({ open, onClose, onConfirm, loading }) => (
    <Dialog open={open} onClose={onClose} PaperProps={{
        sx: {
            background: '#16120d', border: '1px solid rgba(188,150,94,0.2)',
            borderRadius: 2.5, minWidth: { xs: 280, sm: 340 },
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        },
    }}>
        <DialogTitle sx={{ color: 'white', fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 700, pb: 0.5 }}>
            Cancel this order?
        </DialogTitle>
        <DialogContent>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.8rem', sm: '0.85rem' }, lineHeight: 1.6 }}>
                This action cannot be undone. The order will be marked as cancelled immediately.
            </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={onClose} size="small" sx={{
                color: 'rgba(255,255,255,0.45)', textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5,
                fontSize: '0.8rem', px: 2,
                '&:hover': { background: 'rgba(255,255,255,0.04)' },
            }}>
                Go Back
            </Button>
            <Button onClick={onConfirm} disabled={loading} size="small" sx={{
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
                color: '#ef4444', textTransform: 'none', borderRadius: 1.5, fontWeight: 700,
                fontSize: '0.8rem', px: 2.5,
                '&:hover': { background: 'rgba(239,68,68,0.22)' },
                '&:disabled': { opacity: 0.45 },
            }}>
                {loading ? <><CircularProgress size={12} color="inherit" sx={{ mr: 1 }} />Cancelling…</> : 'Yes, Cancel Order'}
            </Button>
        </DialogActions>
    </Dialog>
);

// ── Tracking Timeline ─────────────────────────────────────────────────────────
const TrackingTimeline = ({ checkpoints }) => (
    <Box sx={{ mt: 1.5, pl: 0.5 }}>
        {checkpoints.map((cp, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1.5, pb: idx === checkpoints.length - 1 ? 0 : 1.5 }}>
                <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                    background: idx === 0 ? '#bc965e' : 'rgba(188,150,94,0.25)',
                }} />
                <Box>
                    <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: idx === 0 ? 700 : 500 }}>
                        {cp.status}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }}>
                        {cp.location} · {fmtDate(cp.date)}
                    </Typography>
                </Box>
            </Box>
        ))}
    </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────
const OrderDetail = () => {
    const { billNumber } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [order, setOrder] = useState(location.state?.order || null);
    const [loading, setLoading] = useState(true);
  
    const [tracking, setTracking] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);

    const fetchTracking = useCallback(async () => {
        try {
            setTrackingLoading(true);
            const { data } = await API.get(`/orders/${billNumber}/track`);
            if (data.success) setTracking(data.data);
        } catch {
            // silent fail — tracking is a nice-to-have, don't toast-spam the user
        } finally {
            setTrackingLoading(false);
        }
    }, [billNumber]);

    const fetchOrderDetail = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await API.get(`/orders/${billNumber}`);
            if (data.success) setOrder(data.data);
            else { toast.error('Order not found'); navigate('/orders'); }
        } catch {
            toast.error('Failed to load order details');
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    }, [billNumber, navigate]);

    useEffect(() => { fetchOrderDetail(); }, [fetchOrderDetail]);

    // Fetch tracking only once we have an order with an AWB code
    useEffect(() => {
        if (order?.AWBCode) fetchTracking();
    }, [order?.AWBCode, fetchTracking]);

  

    const status = order?.OrderStatus || 'Pending';
    const items = order?.items || [];

    const canCancel = useMemo(
        () => order && !NON_CANCELLABLE.includes(order.OrderStatus),
        [order]
    );

    const discount = useMemo(() => {
        if (!order) return 0;
        const couponDiscount = parseFloat(order.CouponDiscount || 0);
        const calculated = parseFloat(order.SubTotal || 0) - parseFloat(order.TotalAmount || 0);
        if (couponDiscount > 0) return couponDiscount;
        if (order.UsedCouponCode && calculated > 0) return calculated;
        return 0;
    }, [order]);

    if (loading && !order) return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                <CircularProgress size={34} className="glass-loading" />
                <Typography variant="body2" className="glass-text-secondary">Loading order…</Typography>
            </Box>
        </Container>
    );

    if (!order) return null;

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>

            {/* ── Top bar ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: { xs: 2.5, sm: 3 } }}>
                <IconButton onClick={() => navigate('/orders')} size="small" sx={{
                    color: 'rgba(188, 150, 94, 0.88)',
                    border: '1px solid rgba(188, 150, 94, 0.97)',
                    borderRadius: 1.5, width: 34, height: 34,
                    '&:hover': { background: 'rgba(188,150,94,0.08)', color: '#ffffff', border: '1px solid #ffffff' },
                }}>
                    <BackIcon sx={{ fontSize: 17 }} />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontFamily: 'monospace', fontWeight: 800, color: '#bc965e',
                            fontSize: { xs: '1rem', sm: '1.15rem' }, letterSpacing: '0.04em',
                        }}>
                            #{order.BillId}
                        </Typography>
                        <StatusChip status={status} />
                    </Box>
                    <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#ffffff', mt: 0.2 }}>
                        Placed on {fmtDate(order.CreatedAt)}
                    </Typography>
                </Box>

                
            </Box>

            {/* ── Order Summary ── */}
            {/* ── Order Summary ── */}
            <Section title="Order Summary" icon={<ReceiptIcon />}>
                <InfoRow label="Order Number" value={`#${order.BillId}`} mono copy />
                <InfoRow label="Date" value={fmtDate(order.CreatedAt)} />
                <InfoRow label="Status" value={<StatusChip status={status} />} last />
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '2px solid rgba(188,150,94,0.1)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>Subtotal</Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#ffffff', fontFamily: 'monospace' }}>
                            ₹{fmt(order.SubTotal || order.TotalAmount)}
                        </Typography>
                    </Box>

                    {/* ── Shipping ── */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>Shipping</Typography>
                        <Typography sx={{
                            fontSize: '0.82rem',
                            color: parseFloat(order.ShippingCharge || 0) === 0 ? '#4caf50' : '#ffffff',
                            fontFamily: 'monospace',
                            fontWeight: parseFloat(order.ShippingCharge || 0) === 0 ? 600 : 400,
                        }}>
                            {parseFloat(order.ShippingCharge || 0) === 0 ? 'Free' : `₹${fmt(order.ShippingCharge)}`}
                        </Typography>
                    </Box>

                    {discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, pb: 0.8, borderBottom: '1px solid rgba(188,150,94,0.1)' }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(76,175,80,0.85)' }}>Coupon Discount</Typography>
                                {order.UsedCouponCode && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(76,175,80,0.6)', fontFamily: 'monospace', letterSpacing: '0.03em' }}>
                                        {order.UsedCouponCode}
                                    </Typography>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '0.82rem', color: '#4caf50', fontFamily: 'monospace', fontWeight: 600 }}>
                                -₹{fmt(discount)}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#ffffff', fontWeight: 600 }}>Total Amount</Typography>
                        <Typography sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800,
                            color: '#bc965e', fontFamily: 'monospace', letterSpacing: '0.03em',
                        }}>
                            ₹{fmt(order.TotalAmount)}
                        </Typography>
                    </Box>
                </Box>
            </Section>

            {/* ── Payment Info ── */}
            <Section title="Payment" icon={<PaymentIcon />}>
                <InfoRow
                    label="Payment Method"
                    value={order.payment_method === 'cod' ? '💵 Cash on Delivery' : '💳 Online Payment'}
                />
                <InfoRow
                    label="Payment Status"
                    value={
                        <Chip
                            label={order.payment_status === 'paid' ? 'Paid' : order.payment_status === 'failed' ? 'Failed' : 'Pending'}
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 700,
                                backgroundColor: order.payment_status === 'paid' ? 'rgba(76,175,80,0.12)'
                                    : order.payment_status === 'failed' ? 'rgba(239,68,68,0.12)' : 'rgba(255,152,0,0.12)',
                                border: `1px solid ${order.payment_status === 'paid' ? 'rgba(76,175,80,0.35)'
                                    : order.payment_status === 'failed' ? 'rgba(239,68,68,0.35)' : 'rgba(255,152,0,0.35)'}`,
                                color: order.payment_status === 'paid' ? '#4caf50'
                                    : order.payment_status === 'failed' ? '#ef4444' : '#ff9800',
                            }}
                        />
                    }
                />
                {order.razorpay_payment_id ? (
                    <InfoRow label="Payment ID" value={order.razorpay_payment_id} mono copy last />
                ) : (
                    <InfoRow label="Note" value="Pay cash when order arrives" last />
                )}
            </Section>

            {/* ── Shipping Address ── */}
            {(order.ShippingAddress || order.ShippingCity) && (
                <Section title="Shipping Address" icon={<LocationIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                            background: 'rgba(188,150,94,0.08)', border: '1px solid rgba(188,150,94,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <LocationIcon sx={{ fontSize: 15, color: 'rgba(188,150,94,0.6)' }} />
                        </Box>
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.83rem' }, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                            {[order.ShippingAddress, order.ShippingCity, order.ShippingState, order.ShippingPincode]
                                .filter(Boolean).join(', ')}
                        </Typography>
                    </Box>
                </Section>
            )}

            {/* ── Tracking — always shown, status-aware ── */}
            <Section title="Tracking" icon={<ShippingIcon />}>
                {!order.AWBCode ? (
                    <Typography sx={{ fontSize: '0.8rem', color: '#ffffff', py: 1, }}>
                        {status === 'Cancelled'
                            ? 'This order was cancelled.'
                            : 'Your order is being prepared. Tracking details will appear here once it ships.'}
                    </Typography>
                ) : trackingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : tracking ? (
                    <>
                        <InfoRow label="AWB / Tracking" value={order.AWBCode} mono copy />
                        <InfoRow label="Courier" value={tracking.courierName || '—'} />
                        <InfoRow label="Current Status" value={tracking.currentStatus || '—'} />
                        {tracking.eta && (
                            <InfoRow label="Expected Delivery" value={fmtDate(tracking.eta)} last={!tracking.checkpoints?.length} />
                        )}
                        {tracking.checkpoints?.length > 0 && <TrackingTimeline checkpoints={tracking.checkpoints} />}
                    </>
                ) : (
                    <>
                        <InfoRow label="AWB / Tracking" value={order.AWBCode} mono copy last />
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', mt: 1 }}>
                            Tracking info not available yet.
                        </Typography>
                    </>
                )}
            </Section>

            {/* ── Items ── */}
            <Section title={`Items (${items.length})`} icon={<ShoppingBagIcon />}>
                {items.length === 0 ? (
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', py: 1 }}>
                        No items found.
                    </Typography>
                ) : (
                    <Box sx={{ mx: { xs: -2, sm: -2.5 }, mb: { xs: -1.5, sm: -2 } }}>
                        <ItemsTable items={items} orderTotal={order.TotalAmount} />
                    </Box>
                )}
            </Section>

       
        </Container>
    );
};

export default OrderDetail;